import { ButtonGradientPrimary, Colors, FOCUS_RING_NO_RADIUS, Icon } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import ObjectivePeriodsButton from './ObjectivePeriodsButton';

import type { PeriodDateRange } from '@/constants/ObjectivePeriodLens';

import { GLASS_SURFACE } from '@/components/objectives/objectiveStyles';
import { useNavigate } from '@/hooks/utils/useNavigate';
import {
    employeesWithoutObjectivesPath,
    newObjectivePath,
    stepBackState,
} from '@/navigation/objectivesRoutes';

interface ObjectivesActionsPanelProps {
    canCreateObjectives: boolean;
    canManageOthersObjectives: boolean;
    /** The cycle the creation and the shortcut to the employees without objectives open on. */
    periodId: string;
    /** Its two bounds when that lens is a range of the reader's own rather than a cycle. */
    customRange: PeriodDateRange;
}

/**
 * The glass "Actions" band: on the left, the create button and the shortcut to the employees who
 * have no objective in the selected cycle; on the right edge, the administration shortcut.
 */
export default function ObjectivesActionsPanel({
    canCreateObjectives,
    canManageOthersObjectives,
    periodId,
    customRange,
}: ObjectivesActionsPanelProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <div
            className={`obj-actions-panel ${GLASS_SURFACE} flex flex-wrap items-center justify-between gap-4`}
            data-testid={'objectives-actions-panel'}
        >
            <div className={'flex flex-wrap items-center gap-6'}>
                {canCreateObjectives && (
                    <ButtonGradientPrimary
                        onClick={() =>
                            navigate({
                                path: newObjectivePath({ period: periodId }),
                                state: stepBackState(),
                            })
                        }
                        size={'md'}
                        fitContent
                        leftIcon={
                            <Icon
                                name={'essential-add'}
                                size={'md'}
                                color={Colors.WHITE}
                            />
                        }
                        dataTestId={'create-objective-button'}
                    >
                        {t('objectives.createObjective')}
                    </ButtonGradientPrimary>
                )}

                {canManageOthersObjectives && (
                    <button
                        type={'button'}
                        onClick={() =>
                            navigate({
                                path: employeesWithoutObjectivesPath({
                                    period: periodId,
                                    range: customRange,
                                }),
                                state: stepBackState(),
                            })
                        }
                        className={`${FOCUS_RING_NO_RADIUS} text-navy hover:text-obj-accent flex items-center gap-1.5 rounded-lg px-1 py-1.5 text-[14px] transition-colors hover:underline`}
                        data-testid={'employees-without-objectives-link'}
                    >
                        <Icon
                            name={'users-profile-2user'}
                            size={'md'}
                            color={'inherit'}
                            strokeWidth={1.5}
                        />

                        {t('employeesWithoutObjectives.entryLink')}
                    </button>
                )}
            </div>

            <ObjectivePeriodsButton />
        </div>
    );
}
